import { useId } from 'react';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import { cn } from '@/shared/lib';
import { FieldLabel } from '@/shared/ui/field-label';
import type { SelectOption, SelectProps } from './types';
import { chevronStyles, controlStyles, fieldStyles, itemStyles, popupStyles, triggerStyles } from './styles';

export type { SelectOption, SelectProps, SelectSize } from './types';

/**
 * Custom dropdown backed by `@base-ui/react/select`.
 *
 * The previous implementation rendered a native `<select>`. A native control
 * ships a system-rendered popup whose look differs from the rest of the UI on
 * every platform and is outside the design system. Base UI's parts-based
 * Select renders a real popup we can style, so the trigger, the list, the
 * items and the checkmark all carry our tokens.
 *
 * The public API — `value`, `onChange`, `options`, `label`, `size` — is
 * preserved. Internally, base-ui's `onValueChange(rawValue)` is shaped into
 * a synthetic change event so callers that read `event.target.value` keep
 * working.
 */
export function Select({
  label,
  options,
  hideLabel = false,
  hint,
  error,
  placeholder,
  size = 'md',
  className,
  id,
  value,
  defaultValue,
  onChange,
  disabled,
  required,
  optional = false,
  name,
  ...rest
}: SelectProps) {
  const { fieldId, hintId, errorId, describedBy } = useFieldIds(id, hint, error);

  return (
    <div className={fieldStyles.root}>
      <FieldLabel
        htmlFor={fieldId}
        required={required}
        optional={optional}
        visuallyHidden={hideLabel}
      >
        {label}
      </FieldLabel>
      <SelectPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        name={name}
        disabled={disabled}
        required={required}
        onValueChange={handleValueChange(onChange)}
      >
        <SelectTrigger
          fieldId={fieldId}
          size={size}
          invalid={Boolean(error)}
          describedBy={describedBy}
          className={className}
          placeholder={placeholder}
          options={options}
          rest={rest}
        />
        <SelectPopup options={options} />
      </SelectPrimitive.Root>
      <FieldMessages hintId={hintId} errorId={errorId} hint={hint} error={error} />
    </div>
  );
}

/**
 * The hint and error lines under a control.
 *
 * Extracted so `Select` reads as a list of structural concerns rather than as a
 * label, a trigger, a popup and two conditional paragraphs.
 */
function FieldMessages({
  hintId,
  errorId,
  hint,
  error,
}: {
  hintId?: string;
  errorId?: string;
  hint?: string;
  error?: string;
}) {
  return (
    <>
      {hint ? (
        <p id={hintId} className={fieldStyles.hint}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className={fieldStyles.error}>
          {error}
        </p>
      ) : null}
    </>
  );
}

/**
 * Computes the field id and the joinable `aria-describedby` value from the
 * optional id and the optional hint / error text. Pulled out so the Select
 * function itself reads as a list of structural concerns.
 */
function useFieldIds(id: string | undefined, hint: string | undefined, error: string | undefined) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return { fieldId, hintId, errorId, describedBy };
}

/**
 * Adapts base-ui's `onValueChange(rawValue)` to a synthetic change event so
 * existing callers — which read `event.target.value` — keep working without
 * knowing about base-ui.
 */
function handleValueChange(onChange: SelectProps['onChange']) {
  return (next: unknown) => {
    if (!onChange) return;
    onChange({ target: { value: String(next ?? '') } } as unknown as Parameters<NonNullable<SelectProps['onChange']>>[0]);
  };
}

/** The trigger button — the visible part of the control. */
function SelectTrigger({
  fieldId,
  size,
  invalid,
  describedBy,
  className,
  placeholder,
  options,
  rest,
}: {
  fieldId: string;
  size: 'sm' | 'md';
  invalid: boolean;
  describedBy: string | undefined;
  className: string | undefined;
  placeholder: string | undefined;
  options: SelectOption[];
  rest: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>;
}) {
  return (
    <SelectPrimitive.Trigger
      id={fieldId}
      aria-invalid={invalid ? true : undefined}
      aria-describedby={describedBy}
      {...rest}
      className={cn(
        triggerStyles({ size, invalid }),
        controlStyles({ size, invalid }),
        className,
      )}
    >
      {/*
        THE TRIGGER SHOWS THE OPTION'S LABEL, NOT ITS VALUE.

        `Select.Value` renders the raw value unless it is given a formatter, so a select
        whose value is an id showed that id in the closed trigger — a row of UUIDs where
        an operator expected names. The formatter looks the value up in the options and
        renders the label; an unknown value falls back to itself, so nothing is ever
        rendered as `undefined`.
      */}
      <SelectPrimitive.Value placeholder={placeholder}>
        {(value: unknown) =>
          value === null || value === undefined
            ? placeholder
            : (options.find((option) => option.value === value)?.label ?? String(value))
        }
      </SelectPrimitive.Value>
      <SelectPrimitive.Icon className={chevronStyles}>
        <FiChevronDown aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

/** The portal-and-positioner-wrapped popup that lists the options. */
function SelectPopup({ options }: { options: SelectOption[] }) {
  return (
    <SelectPrimitive.Portal>
      {/*
        `side="bottom"` and `collisionAvoidance="none"` together keep the
        popup pinned below the trigger. The default `flip` behaviour pushes
        the popup above the trigger when the viewport is short, and that
        makes the popup overlap whatever sits above the filter row — tabs,
        page header, anything else. For an admin table that overlap is
        worse than clipping, so we never flip and let the page scroll instead.
      */}
      <SelectPrimitive.Positioner
        side="bottom"
        sideOffset={4}
        collisionAvoidance={{ side: 'none' }}
        alignItemWithTrigger={false}
        className={popupStyles.positioner}
      >
        <SelectPrimitive.Popup className={popupStyles.popup}>
          <SelectPrimitive.List className={popupStyles.list}>
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={itemStyles.item}
              >
                <SelectPrimitive.ItemIndicator className={itemStyles.indicator}>
                  <FiCheck aria-hidden="true" size={14} />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText className={itemStyles.text}>
                  {option.label}
                </SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.List>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}
